import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Search, Filter, Loader2, History, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuditLogsPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('all');
  const [expandedRows, setExpandedRows] = useState({});

  const fetchLogs = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (actionFilter && actionFilter !== 'all') params.append('action', actionFilter);
      const response = await axios.get(`${API_URL}/audit-logs?${params}`);
      setLogs(response.data);
    } catch (error) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [actionFilter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionBadge = (action) => {
    const config = {
      create: { label: 'Created', className: 'bg-blue-100 text-blue-800 border-blue-200' },
      update: { label: 'Updated', className: 'bg-amber-100 text-amber-800 border-amber-200' },
      approve: { label: 'Approved', className: 'bg-green-100 text-green-800 border-green-200' },
      reject: { label: 'Rejected', className: 'bg-red-100 text-red-800 border-red-200' },
      delete: { label: 'Deleted', className: 'bg-gray-100 text-gray-800 border-gray-200' },
    };
    const { label, className } = config[action] || { label: action, className: 'bg-gray-100 text-gray-800' };
    return <Badge variant="outline" className={className}>{label}</Badge>;
  };

  const toggleRow = (id) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const renderChanges = (changes) => {
    if (!changes) return null;

    if (changes.comment) {
      return (
        <div className="p-4 bg-gray-50 rounded-lg">
          <p className="text-sm"><span className="font-medium">Comment:</span> {changes.comment}</p>
        </div>
      );
    }

    if (changes.new_data && !changes.old_data) {
      return (
        <div className="p-4 bg-gray-50 rounded-lg">
          <p className="text-sm font-medium mb-2">New Data:</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
            {Object.entries(changes.new_data).slice(0, 12).map(([key, value]) => (
              <div key={key} className="truncate">
                <span className="text-muted-foreground">{key}:</span>{' '}
                <span className="font-mono">{String(value ?? '—')}</span>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (changes.old_data && changes.new_data) {
      const changedFields = Object.keys(changes.new_data).filter(
        key => changes.old_data[key] !== changes.new_data[key]
      );

      return (
        <div className="p-4 bg-gray-50 rounded-lg space-y-2">
          <p className="text-sm font-medium">Changes:</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left">
                  <th className="pb-2 font-medium text-muted-foreground">Field</th>
                  <th className="pb-2 font-medium text-muted-foreground">Old Value</th>
                  <th className="pb-2 font-medium text-muted-foreground">New Value</th>
                </tr>
              </thead>
              <tbody>
                {changedFields.map(field => (
                  <tr key={field} className="border-t border-gray-200">
                    <td className="py-2 font-medium">{field}</td>
                    <td className="py-2 font-mono text-red-600">{String(changes.old_data[field] ?? '—')}</td>
                    <td className="py-2 font-mono text-green-600">{String(changes.new_data[field] ?? '—')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="audit-logs-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Audit Logs</h1>
          <p className="text-muted-foreground mt-1">Track all changes made to emission factors</p>
        </div>

        {/* Filters */}
        <Card className="border-gray-200">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <Select value={actionFilter} onValueChange={setActionFilter}>
                <SelectTrigger className="w-full sm:w-48 input-eco" data-testid="action-filter">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by action" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  <SelectItem value="create">Created</SelectItem>
                  <SelectItem value="update">Updated</SelectItem>
                  <SelectItem value="approve">Approved</SelectItem>
                  <SelectItem value="reject">Rejected</SelectItem>
                  <SelectItem value="delete">Deleted</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Logs Table */}
        <Card className="border-gray-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-12">
                <History className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-30" />
                <p className="text-muted-foreground">No audit logs found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="table-dense w-8"></TableHead>
                      <TableHead className="table-dense">Timestamp</TableHead>
                      <TableHead className="table-dense">User</TableHead>
                      <TableHead className="table-dense">Action</TableHead>
                      <TableHead className="table-dense">Entity</TableHead>
                      <TableHead className="table-dense">Entity ID</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log) => (
                      <Collapsible key={log.id} asChild open={expandedRows[log.id]}>
                        <>
                          <TableRow className="table-dense" data-testid={`audit-row-${log.id}`}>
                            <TableCell>
                              <CollapsibleTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6"
                                  onClick={() => toggleRow(log.id)}
                                  data-testid={`expand-${log.id}`}
                                >
                                  {expandedRows[log.id] ? (
                                    <ChevronDown className="w-4 h-4" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4" />
                                  )}
                                </Button>
                              </CollapsibleTrigger>
                            </TableCell>
                            <TableCell className="font-mono text-sm">{formatDate(log.timestamp)}</TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium">{log.user_name}</p>
                                <p className="text-xs text-muted-foreground">{log.user_email}</p>
                              </div>
                            </TableCell>
                            <TableCell>{getActionBadge(log.action)}</TableCell>
                            <TableCell className="capitalize">{log.entity_type.replace('_', ' ')}</TableCell>
                            <TableCell className="font-mono text-sm">{log.entity_id.slice(0, 8)}...</TableCell>
                          </TableRow>
                          <CollapsibleContent asChild>
                            <tr>
                              <td colSpan={6} className="p-0">
                                <div className="px-4 pb-4">
                                  {renderChanges(log.changes)}
                                </div>
                              </td>
                            </tr>
                          </CollapsibleContent>
                        </>
                      </Collapsible>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
